import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { posApi } from '../api/pos.api';
import BarcodeScannerModal from '../components/ui/BarcodeScannerModal';
import DLCInput from '../components/ui/DLCInput';

interface InventorySession {
  id: string;
  reference: string;
  name: string;
  status: 'DRAFT' | 'VALIDATED' | 'CANCELLED';
  started_at: string;
  completed_at?: string;
  pdf_url?: string;
  total_lots: number;
  counted_lots: number;
  progress: number;
  lots_with_difference?: number;
  theoretical_value_eur?: string;
  counted_value_eur?: string;
  adjustment_value_eur?: string;
  scope_type?: 'FULL' | 'RAYON';
  scope_label?: string | null;
  counts?: InventoryCount[];
}

interface InventoryCount {
  id: string;
  lot_id: string;
  lot_number: string;
  product_name: string;
  product_code?: string;
  product_barcode?: string;
  category?: string;
  expiry_date?: string;
  unit?: string;
  is_weighted?: boolean;
  theoretical_backroom: number;
  theoretical_display: number;
  counted_backroom?: number;
  counted_display?: number;
  diff_backroom?: number;
  diff_display?: number;
  purchase_price_cents?: number;
  theoretical_value?: number;
  counted_value?: number;
  adjustment_value?: number;
  is_counted: boolean;
  notes?: string;
  adjustment_reason?: string | null;
}

interface CoverageItem {
  rayon_id: string;
  rayon_name: string;
  cadence_days: number;
  last_inventory_at: string | null;
  last_inventory_reference: string | null;
  days_since: number | null;
  overdue: boolean;
}

interface RayonOption {
  id: string;
  label: string;
}

// Motifs d'écart d'inventaire (codes alignés avec le backend)
const ADJUSTMENT_REASONS: { value: string; label: string }[] = [
  { value: 'BREAKAGE', label: 'Casse' },
  { value: 'EXPIRY', label: 'Péremption' },
  { value: 'THEFT', label: 'Vol / démarque inconnue' },
  { value: 'RECEPTION_ERROR', label: 'Erreur de réception' },
  { value: 'ENTRY_ERROR', label: 'Erreur de saisie' },
  { value: 'FIELD_ADJUSTMENT', label: 'Ajustement production (champ)' },
  { value: 'OTHER', label: 'Autre' },
];

// Regroupement par produit pour la vue terrain
interface ProductGroup {
  productName: string;
  productCode?: string;
  category?: string;
  unit?: string;
  isWeighted?: boolean;
  counts: InventoryCount[];
  totalTheoreticalBackroom: number;
  totalTheoreticalDisplay: number;
  totalCountedBackroom: number;
  totalCountedDisplay: number;
  allCounted: boolean;
  anyCounted: boolean;
}

type FilterMode = 'to_count' | 'all' | 'not_counted' | 'counted';

export default function Inventory() {
  const [sessions, setSessions] = useState<InventorySession[]>([]);
  const [currentSession, setCurrentSession] = useState<InventorySession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  // Par défaut on n'affiche QUE le travail réel : les lignes non comptées portant
  // du stock théorique. Les lignes à zéro (79 sur 140 en Légumes, 88 sur 107 en
  // Boulangerie) restent dans l'inventaire — c'est par elles qu'une marchandise
  // trouvée en rayon entre au stock — mais elles ne noient plus la liste.
  const [filterMode, setFilterMode] = useState<FilterMode>('to_count');

  // Un produit compté SORT du filtre « à compter » à la seconde où il est compté :
  // la carte s'évanouit sous la main, on ne peut plus relire ce qu'on vient de
  // saisir ni se corriger. On garde donc visibles, le temps de la visite, les
  // produits comptés ici — la bordure verte dit déjà qu'ils sont faits. La liste
  // se nettoie au changement de filtre, de rayon ou au rechargement.
  const [comptesDeLaVisite, setComptesDeLaVisite] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [creating, setCreating] = useState(false);
  const [validating, setValidating] = useState(false);

  // Poser un motif à la validation : le sélecteur d'une ligne n'apparaît qu'une
  // fois la ligne comptée, et en fin de comptage on n'a plus envie de repasser
  // sur chaque carte. Cette étape rassemble donc, au moment de valider, tous les
  // écarts encore sans cause — c'est là qu'on a la vue d'ensemble pour trancher.
  const [motifsOuverts, setMotifsOuverts] = useState(false);
  const [motifsEnCours, setMotifsEnCours] = useState<Record<string, string>>({});
  const [motifEnMasse, setMotifEnMasse] = useState('');
  const [enregistrementMotifs, setEnregistrementMotifs] = useState(false);
  const [confirmingZeros, setConfirmingZeros] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const productRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Inventaire tournant : couverture par rayon, rayons, produits sans catégorie
  const [coverage, setCoverage] = useState<CoverageItem[]>([]);
  const [rayons, setRayons] = useState<RayonOption[]>([]);
  const [uncategorized, setUncategorized] = useState<{ count: number; products: { id: string; name: string }[] }>({ count: 0, products: [] });
  // Modal de création (choix du périmètre)
  const [showNewModal, setShowNewModal] = useState(false);
  const [scopeChoice, setScopeChoice] = useState<'FULL' | 'RAYON'>('RAYON');
  const [selectedRayonIds, setSelectedRayonIds] = useState<string[]>([]);

  useEffect(() => {
    loadSessions();
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [cov, cats, uncat] = await Promise.all([
        posApi.getInventoryCoverage(),
        posApi.getCategories(),
        posApi.getUncategorizedProducts(),
      ]);
      setCoverage(cov || []);
      // Rayons = catégories niveau 0 (parent_id null)
      const rayonOpts: RayonOption[] = (cats || [])
        .filter((c: any) => !c.parent_id)
        .map((c: any) => ({ id: c.id, label: c.label || c.name }));
      setRayons(rayonOpts);
      setUncategorized(uncat || { count: 0, products: [] });
    } catch (err) {
      // Le dashboard est secondaire : ne pas bloquer la page si l'appel échoue
      console.error('Erreur chargement dashboard inventaire', err);
    }
  };

  // Écouter les scans code-barres (ils émulent une saisie clavier)
  useEffect(() => {
    if (!currentSession || currentSession.status !== 'DRAFT') return;

    let buffer = '';
    let lastKeyTime = 0;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Les scanners tapent très vite (< 50ms entre touches)
      const now = Date.now();
      const target = e.target as HTMLElement;

      // Ignorer si focus dans un input (sauf le champ recherche)
      if (target.tagName === 'INPUT' && target !== searchInputRef.current) return;

      if (now - lastKeyTime > 100) {
        buffer = '';
      }
      lastKeyTime = now;

      if (e.key === 'Enter' && buffer.length >= 3) {
        e.preventDefault();
        handleBarcodeScan(buffer);
        buffer = '';
      } else if (e.key.length === 1) {
        buffer += e.key;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSession]);

  const handleBarcodeScan = useCallback((barcode: string) => {
    if (!currentSession?.counts) return;

    const needle = barcode.toLowerCase();
    const match = currentSession.counts.find(
      (c) =>
        c.product_barcode?.toLowerCase() === needle ||
        c.product_code?.toLowerCase() === needle ||
        c.lot_number?.toLowerCase() === needle
    );

    if (match) {
      setSearchQuery(match.product_name);
      setTimeout(() => {
        const key = match.product_name;
        const el = productRefs.current[key];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-4', 'ring-emerald-400');
          setTimeout(() => el.classList.remove('ring-4', 'ring-emerald-400'), 2000);
        }
      }, 100);
    } else {
      setSearchQuery(barcode);
    }
  }, [currentSession]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const data = await posApi.listInventorySessions();
      setSessions(data);

      const draftSession = data.find((s: InventorySession) => s.status === 'DRAFT');
      if (draftSession) {
        await loadSession(draftSession.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId: string) => {
    try {
      setLoading(true);
      const data = await posApi.getInventorySession(sessionId);
      setCurrentSession(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!currentSession) return;
    setRefreshing(true);
    try {
      await loadSession(currentSession.id);
    } finally {
      setRefreshing(false);
    }
  };

  const openNewModal = () => {
    setScopeChoice(rayons.length > 0 ? 'RAYON' : 'FULL');
    setSelectedRayonIds([]);
    setShowNewModal(true);
  };

  const toggleRayon = (id: string) => {
    setSelectedRayonIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Compléter après coup l'explication d'un écart, sur un inventaire validé.
  const [annotating, setAnnotating] = useState<string | null>(null);
  const [reissuing, setReissuing] = useState(false);

  const handleAnnotate = async (countId: string, produit: string, ecartEur: string) => {
    const texte = window.prompt(
      `Expliquer l'écart — ${produit} (${ecartEur})\n\n` +
        `Votre explication sera AJOUTÉE à la fiche, datée et signée. ` +
        `Rien n'est réécrit : ce qui a été noté au comptage reste.`,
      '',
    );
    if (!texte || !texte.trim() || !currentSession) return;
    try {
      setAnnotating(countId);
      await posApi.annotateInventoryCount(currentSession.id, countId, { note: texte.trim() });
      await loadSession(currentSession.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erreur lors de l'ajout");
    } finally {
      setAnnotating(null);
    }
  };

  const handleReissuePdf = async () => {
    if (!currentSession) return;
    if (
      !window.confirm(
        "Rééditer le PDF avec les explications ajoutées depuis la validation ?\n\n" +
          "Le document d'origine reste archivé : la réédition part sous un nom daté " +
          'et porte la mention de sa réédition.',
      )
    )
      return;
    try {
      setReissuing(true);
      await posApi.reissueInventoryPdf(currentSession.id);
      await loadSession(currentSession.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la réédition');
    } finally {
      setReissuing(false);
    }
  };

  const handleCreateSession = async () => {
    if (scopeChoice === 'RAYON' && selectedRayonIds.length === 0) {
      alert('Sélectionnez au moins un rayon, ou choisissez « Inventaire complet ».');
      return;
    }
    try {
      setCreating(true);
      const isFull = scopeChoice === 'FULL';
      const rayonLabels = rayons
        .filter((r) => selectedRayonIds.includes(r.id))
        .map((r) => r.label)
        .join(', ');
      const session = await posApi.createInventorySession({
        name: isFull
          ? `Inventaire complet ${new Date().toLocaleDateString('fr-FR')}`
          : `Inventaire ${rayonLabels} — ${new Date().toLocaleDateString('fr-FR')}`,
        scope_type: isFull ? 'FULL' : 'RAYON',
        scope_category_ids: isFull ? [] : selectedRayonIds,
      });
      setShowNewModal(false);
      setCurrentSession(session);
      await loadSessions();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateCadence = async (rayonId: string, cadenceDays: number | null) => {
    try {
      await posApi.updateCategoryCadence(rayonId, cadenceDays);
      setCoverage((prev) =>
        prev.map((c) =>
          c.rayon_id === rayonId
            ? {
                ...c,
                cadence_days: cadenceDays ?? 180,
                overdue: c.days_since === null ? true : c.days_since > (cadenceDays ?? 180),
              }
            : c,
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la mise à jour de la cadence');
    }
  };

  const handleUpdateMeta = async (
    count: InventoryCount,
    meta: { adjustment_reason?: string; notes?: string },
  ) => {
    if (!currentSession) return;
    // Renvoie les valeurs comptées actuelles + le motif/remarque (sans changer le stock)
    try {
      await posApi.updateInventoryCount(currentSession.id, count.id, {
        counted_backroom: Math.max(0, count.counted_backroom ?? 0),
        counted_display: Math.max(0, count.counted_display ?? 0),
        notes: meta.notes ?? count.notes ?? undefined,
        adjustment_reason: meta.adjustment_reason ?? count.adjustment_reason ?? undefined,
      });
      // Mise à jour optimiste locale
      setCurrentSession((prev) => {
        if (!prev || !prev.counts) return prev;
        return {
          ...prev,
          counts: prev.counts.map((c) =>
            c.id === count.id
              ? {
                  ...c,
                  notes: meta.notes ?? c.notes,
                  adjustment_reason: meta.adjustment_reason ?? c.adjustment_reason,
                }
              : c,
          ),
        };
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la mise à jour du motif');
    }
  };

  const handleUpdateCount = async (count: InventoryCount, backroom: number, display: number) => {
    if (!currentSession) return;

    const countId = count.id;
    setSaving((prev) => ({ ...prev, [countId]: true }));
    setSaved((prev) => ({ ...prev, [countId]: false }));

    try {
      await posApi.updateInventoryCount(currentSession.id, countId, {
        counted_backroom: Math.max(0, backroom),
        counted_display: Math.max(0, display),
      });

      if (count.product_name) {
        const nom = count.product_name;
        setComptesDeLaVisite((prev) => (prev.has(nom) ? prev : new Set(prev).add(nom)));
      }

      // Mise à jour locale sans recharger toute la session
      setCurrentSession((prev) => {
        if (!prev?.counts) return prev;
        const newCounts = prev.counts.map((c) => {
          if (c.id !== countId) return c;
          const newBackroom = Math.max(0, backroom);
          const newDisplay = Math.max(0, display);
          return {
            ...c,
            counted_backroom: newBackroom,
            counted_display: newDisplay,
            diff_backroom: newBackroom - c.theoretical_backroom,
            diff_display: newDisplay - c.theoretical_display,
            is_counted: true,
          };
        });
        const countedLots = newCounts.filter((c) => c.is_counted).length;
        return {
          ...prev,
          counts: newCounts,
          counted_lots: countedLots,
          progress: prev.total_lots > 0 ? Math.round((countedLots / prev.total_lots) * 100) : 0,
        };
      });

      setSaved((prev) => ({ ...prev, [countId]: true }));
      setTimeout(() => setSaved((prev) => ({ ...prev, [countId]: false })), 1500);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur sauvegarde');
    } finally {
      setSaving((prev) => ({ ...prev, [countId]: false }));
    }
  };

  const handleUpdateExpiry = async (count: InventoryCount, isoDate: string | null) => {
    if (!currentSession) return;

    const previous = count.expiry_date ?? null;
    const next = isoDate || null;
    if (previous === next) return;

    setCurrentSession((prev) => {
      if (!prev?.counts) return prev;
      return {
        ...prev,
        counts: prev.counts.map((c) =>
          c.id === count.id ? { ...c, expiry_date: next ?? undefined } : c
        ),
      };
    });

    try {
      await posApi.updateLot(count.lot_id, { expiry_date: next });
    } catch (err) {
      setCurrentSession((prev) => {
        if (!prev?.counts) return prev;
        return {
          ...prev,
          counts: prev.counts.map((c) =>
            c.id === count.id ? { ...c, expiry_date: previous ?? undefined } : c
          ),
        };
      });
      alert(err instanceof Error ? err.message : 'Erreur mise à jour DLC');
    }
  };

  // Lignes encore à compter dont le stock théorique est déjà nul : ce sont les
  // produits saisonniers absents (hiver notamment). Sur Fruits + Légumes ils
  // représentent environ un quart des lignes, et validateSession refuse de
  // clôturer tant qu'elles ne sont pas comptées — d'où ce raccourci.
  const zeroPendingCount = useMemo(
    () =>
      (currentSession?.counts ?? []).filter(
        (c) => !c.is_counted && c.theoretical_backroom === 0 && c.theoretical_display === 0,
      ).length,
    [currentSession],
  );

  const handleCountZeroLines = async () => {
    if (!currentSession || zeroPendingCount === 0) return;
    if (
      !confirm(
        `Confirmer ${zeroPendingCount} produit(s) comme absents (stock 0) ?\n\n` +
          `Seules les lignes non comptées dont le stock théorique est déjà à 0 sont concernées. ` +
          `Elles restent dans l'inventaire, elles sont simplement validées à 0.\n\n` +
          `À faire en dernier : comptez d'abord ce qui est physiquement présent.`,
      )
    )
      return;

    try {
      setConfirmingZeros(true);
      const { confirmed } = await posApi.countZeroInventoryLines(currentSession.id, {});
      await loadSession(currentSession.id);
      alert(`${confirmed} produit(s) confirmé(s) absents.`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la confirmation');
    } finally {
      setConfirmingZeros(false);
    }
  };

  // Un écart sans cause part en démarque INCONNUE ; « erreur de réception » et
  // « erreur de saisie » en sont au contraire exclues (la marchandise n'a jamais
  // été là). Le motif n'est donc pas décoratif : il décide de ce que le rapport
  // de pertes compte.
  const ecartsSansMotif = useMemo(() => {
    if (!currentSession?.counts) return [];
    return currentSession.counts
      .filter(
        (c) =>
          c.is_counted &&
          ((c.diff_backroom ?? 0) !== 0 || (c.diff_display ?? 0) !== 0) &&
          !c.adjustment_reason,
      )
      .sort(
        (a, b) =>
          Math.abs((b.diff_backroom ?? 0) + (b.diff_display ?? 0)) -
          Math.abs((a.diff_backroom ?? 0) + (a.diff_display ?? 0)),
      );
  }, [currentSession]);

  const enregistrerMotifs = async () => {
    const aPoser = Object.entries(motifsEnCours).filter(([, v]) => v);
    if (aPoser.length === 0) return;
    setEnregistrementMotifs(true);
    try {
      for (const [countId, motif] of aPoser) {
        const ligne = currentSession?.counts?.find((c) => c.id === countId);
        if (ligne) await handleUpdateMeta(ligne, { adjustment_reason: motif });
      }
      setMotifsEnCours({});
      setMotifEnMasse('');
    } finally {
      setEnregistrementMotifs(false);
    }
  };

  const handleValidateSession = async () => {
    if (!currentSession) return;

    // Première intention : on propose de qualifier les écarts orphelins. On
    // n'impose rien — un inventaire du 31/12 ne doit pas rester bloqué sur un
    // menu déroulant — mais on ne laisse pas non plus filer sans le dire.
    if (ecartsSansMotif.length > 0 && !motifsOuverts) {
      setMotifsEnCours({});
      setMotifEnMasse('');
      setMotifsOuverts(true);
      return;
    }

    if (!confirm('Valider cet inventaire ? Les stocks seront ajustés définitivement.')) return;
    setMotifsOuverts(false);

    try {
      setValidating(true);
      await posApi.validateInventorySession(currentSession.id, {});
      await loadSessions();
      await loadDashboard();
      setCurrentSession(null);
      alert('Inventaire validé avec succès !');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la validation');
    } finally {
      setValidating(false);
    }
  };

  const handleCancelSession = async () => {
    if (!currentSession) return;
    if (!confirm('Annuler cet inventaire ?')) return;

    try {
      await posApi.cancelInventorySession(currentSession.id);
      await loadSessions();
      setCurrentSession(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erreur lors de l'annulation");
    }
  };

  // Catégories uniques
  const categories = useMemo(() => {
    if (!currentSession?.counts) return [];
    const cats = new Set(currentSession.counts.map((c) => c.category).filter(Boolean));
    return Array.from(cats).sort() as string[];
  }, [currentSession]);

  // Le filtre suit l'état de la session ouverte : « à compter » quand il y a du travail,
  // « tous » quand on vient seulement consulter une session close (sinon la liste
  // s'affiche vide). Recalé à chaque changement de session : un filtre hérité de la
  // session précédente donnerait une liste incompréhensible.
  const openSessionId = currentSession?.id;
  const openSessionStatus = currentSession?.status;
  useEffect(() => {
    if (!openSessionId) return;
    setFilterMode(openSessionStatus === 'DRAFT' ? 'to_count' : 'all');
  }, [openSessionId, openSessionStatus]);

  // Changer de filtre, de session ou de rayon repart d'une liste propre.
  useEffect(() => {
    setComptesDeLaVisite(new Set());
  }, [filterMode, openSessionId, filterCategory]);

  // Regrouper les lots par produit
  const productGroups = useMemo((): ProductGroup[] => {
    if (!currentSession?.counts) return [];

    const groups = new Map<string, ProductGroup>();

    for (const count of currentSession.counts) {
      const key = count.product_name || 'Sans nom';

      // Filtres
      const matchesSearch =
        !searchQuery ||
        count.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        count.lot_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        count.product_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        count.product_barcode?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = !filterCategory || count.category === filterCategory;

      if (!matchesSearch || !matchesCategory) continue;

      if (!groups.has(key)) {
        groups.set(key, {
          productName: key,
          productCode: count.product_code,
          category: count.category,
          unit: count.unit,
          isWeighted: count.is_weighted,
          counts: [],
          totalTheoreticalBackroom: 0,
          totalTheoreticalDisplay: 0,
          totalCountedBackroom: 0,
          totalCountedDisplay: 0,
          allCounted: true,
          anyCounted: false,
        });
      }

      const group = groups.get(key)!;
      group.counts.push(count);
      group.totalTheoreticalBackroom += count.theoretical_backroom;
      group.totalTheoreticalDisplay += count.theoretical_display;
      group.totalCountedBackroom += count.counted_backroom ?? 0;
      group.totalCountedDisplay += count.counted_display ?? 0;
      if (!count.is_counted) group.allCounted = false;
      if (count.is_counted) group.anyCounted = true;
    }

    let result = Array.from(groups.values());

    // Filtre comptés/non comptés
    if (filterMode === 'to_count') {
      result = result.filter(
        (g) =>
          comptesDeLaVisite.has(g.productName) ||
          g.counts.some(
            (c) => !c.is_counted && c.theoretical_backroom + c.theoretical_display > 0,
          ),
      );
    } else if (filterMode === 'not_counted') {
      result = result.filter((g) => !g.allCounted);
    } else if (filterMode === 'counted') {
      result = result.filter((g) => g.anyCounted);
    }

    return result.sort((a, b) => a.productName.localeCompare(b.productName, 'fr'));
  }, [currentSession, searchQuery, filterCategory, filterMode, comptesDeLaVisite]);

  // Stats
  const stats = useMemo(() => {
    if (!currentSession?.counts) return { total: 0, counted: 0, notCounted: 0 };
    const uniqueProducts = new Set(currentSession.counts.map((c) => c.product_name));
    const countedProducts = new Set(
      currentSession.counts.filter((c) => c.is_counted).map((c) => c.product_name)
    );
    // Un produit est "compté" si TOUS ses lots sont comptés
    const fullyCountedProducts = new Set(
      [...countedProducts].filter((name) => {
        const lotsForProduct = currentSession.counts!.filter((c) => c.product_name === name);
        return lotsForProduct.every((c) => c.is_counted);
      })
    );
    // Produits portant au moins une ligne non comptée AVEC du stock théorique :
    // c'est le seul chiffre qui décrit le travail restant devant l'étal.
    const toCount = new Set(
      currentSession.counts
        .filter((c) => !c.is_counted && c.theoretical_backroom + c.theoretical_display > 0)
        .map((c) => c.product_name),
    );
    return {
      total: uniqueProducts.size,
      counted: fullyCountedProducts.size,
      notCounted: uniqueProducts.size - fullyCountedProducts.size,
      toCount: toCount.size,
    };
  }, [currentSession]);

  if (loading && !currentSession) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Liste des sessions
  if (!currentSession) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inventaire</h1>
            <p className="text-gray-500 mt-1">Sessions d'inventaire physique</p>
          </div>
          <button
            onClick={openNewModal}
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl text-lg font-semibold hover:bg-emerald-700 disabled:opacity-50"
          >
            Nouvel Inventaire
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
        )}

        {/* Bannière : produits sans catégorie (jamais inventoriés par rayon) */}
        {uncategorized.count > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  {uncategorized.count} produit{uncategorized.count > 1 ? 's' : ''} sans catégorie
                </div>
                <div className="text-sm text-amber-800 mt-0.5">
                  Ces produits ne seront <strong>jamais comptés</strong> par un inventaire par rayon.
                  Assignez-leur une catégorie depuis la fiche produit pour garantir un inventaire complet.
                </div>
                <div className="text-xs text-amber-700 mt-1 line-clamp-2">
                  {uncategorized.products.slice(0, 12).map((p) => p.name).join(' · ')}
                  {uncategorized.count > 12 ? ` … (+${uncategorized.count - 12})` : ''}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tableau de bord : couverture par rayon (inventaire tournant) */}
        {coverage.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">Couverture par rayon</h2>
              <span className="text-xs text-gray-500">
                Cadence = délai cible entre 2 inventaires. Orange = en retard.
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {coverage.map((c) => (
                <div
                  key={c.rayon_id}
                  className={`border rounded-lg p-3 ${
                    c.overdue ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{c.rayon_name}</span>
                    {c.overdue && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded bg-orange-200 text-orange-800">
                        En retard
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {c.last_inventory_at
                      ? `Dernier : ${new Date(c.last_inventory_at).toLocaleDateString('fr-FR')}${
                          c.days_since != null ? ` (il y a ${c.days_since} j)` : ''
                        }`
                      : 'Jamais inventorié'}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <span className="text-xs text-gray-500">Tous les</span>
                    <input
                      type="number"
                      min="1"
                      defaultValue={c.cadence_days}
                      onBlur={(e) => {
                        const v = parseInt(e.target.value, 10);
                        const next = isNaN(v) || v <= 0 ? null : v;
                        if ((next ?? 180) !== c.cadence_days) handleUpdateCadence(c.rayon_id, next);
                      }}
                      className="w-16 text-center border border-gray-300 rounded px-1 py-0.5 text-sm"
                    />
                    <span className="text-xs text-gray-500">jours</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progression</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Aucun inventaire. Créez votre premier inventaire pour commencer.
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {session.reference}
                      <span
                        className={`ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                          session.scope_type === 'RAYON'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {session.scope_type === 'RAYON' ? 'Rayon' : 'Complet'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{session.scope_label || session.name}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          session.status === 'VALIDATED'
                            ? 'bg-green-100 text-green-800'
                            : session.status === 'DRAFT'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {session.status === 'VALIDATED' ? 'Validé' : session.status === 'DRAFT' ? 'En cours' : 'Annulé'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {new Date(session.started_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-emerald-600 h-2 rounded-full"
                            style={{ width: `${session.progress || 0}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600">
                          {session.counted_lots || 0}/{session.total_lots}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {session.status === 'DRAFT' && (
                        <button
                          onClick={() => loadSession(session.id)}
                          className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
                        >
                          Continuer
                        </button>
                      )}
                      {session.status === 'VALIDATED' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => loadSession(session.id)}
                            className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                          >
                            Voir
                          </button>
                          {session.pdf_url && (
                            <button
                              type="button"
                              onClick={() => void ouvrirDocumentPrive(session.pdf_url!)}
                              className="px-3 py-1.5 bg-blue-100 text-blue-700 text-sm rounded-lg hover:bg-blue-200"
                            >
                              PDF
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Modal : nouveau périmètre d'inventaire */}
        {showNewModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-5 border-b">
                <h2 className="text-lg font-bold text-gray-900">Nouvel inventaire</h2>
                <p className="text-sm text-gray-500 mt-0.5">Choisissez le périmètre à compter.</p>
              </div>
              <div className="p-5 space-y-4">
                {/* Choix du type */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setScopeChoice('RAYON')}
                    className={`p-3 rounded-lg border-2 text-left ${
                      scopeChoice === 'RAYON' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">Par rayon</div>
                    <div className="text-xs text-gray-500 mt-0.5">Comptage partiel, rapide (tournant)</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setScopeChoice('FULL')}
                    className={`p-3 rounded-lg border-2 text-left ${
                      scopeChoice === 'FULL' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">Complet</div>
                    <div className="text-xs text-gray-500 mt-0.5">Tout le catalogue (obligatoire 31/12)</div>
                  </button>
                </div>

                {/* Sélection des rayons */}
                {scopeChoice === 'RAYON' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Rayons à inventorier</span>
                      <span className="text-xs text-gray-400">{selectedRayonIds.length} sélectionné{selectedRayonIds.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                      {rayons.map((r) => (
                        <label
                          key={r.id}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${
                            selectedRayonIds.includes(r.id) ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedRayonIds.includes(r.id)}
                            onChange={() => toggleRayon(r.id)}
                            className="w-4 h-4 accent-emerald-600"
                          />
                          <span className="text-sm text-gray-800">{r.label}</span>
                        </label>
                      ))}
                      {rayons.length === 0 && (
                        <div className="col-span-2 text-sm text-gray-500">Aucun rayon disponible.</div>
                      )}
                    </div>
                  </div>
                )}

                {scopeChoice === 'FULL' && (
                  <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
                    Tous les produits actifs du catalogue seront inclus. Cet inventaire réinitialise
                    la couverture de tous les rayons.
                  </div>
                )}
              </div>
              <div className="p-5 border-t flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleCreateSession}
                  disabled={creating || (scopeChoice === 'RAYON' && selectedRayonIds.length === 0)}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {creating ? 'Création...' : 'Démarrer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── SESSION EN COURS — MODE COMPTAGE TERRAIN ──
  return (
    <div className="p-4 space-y-4 max-w-5xl mx-auto">
      {/* Header compact */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{currentSession.reference}</h1>
            <p className="text-sm text-gray-500">{currentSession.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium flex items-center gap-2"
              title="Recharger depuis le serveur (changement de poste)"
            >
              <svg className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing ? 'Sync...' : 'Rafraîchir'}
            </button>
            <button
              onClick={() => setCurrentSession(null)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Retour
            </button>
          </div>
        </div>

        {/* Barre de progression + stats */}
        <div className="mt-3 flex items-center gap-4">
          <div className="flex-1">
            <div className="bg-gray-200 rounded-full h-3">
              <div
                className="bg-emerald-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${currentSession.progress}%` }}
              />
            </div>
          </div>
          <div className="text-sm font-semibold text-gray-700 whitespace-nowrap">
            {stats.counted} / {stats.total} produits
          </div>
        </div>

        {filterMode === 'to_count' && zeroPendingCount > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            {zeroPendingCount} ligne{zeroPendingCount > 1 ? 's' : ''} sans stock théorique
            {zeroPendingCount > 1 ? ' sont masquées' : ' est masquée'} — à confirmer absente
            {zeroPendingCount > 1 ? 's' : ''} en fin de comptage, ou{' '}
            <button
              type="button"
              onClick={() => setFilterMode('all')}
              className="underline text-gray-600 hover:text-gray-900"
            >
              à afficher
            </button>{' '}
            si vous trouvez l'un de ces articles en rayon.
          </p>
        )}

        {/* Actions session */}
        {currentSession.status === 'DRAFT' && (
          <div className="mt-3 flex items-center gap-2 pt-3 border-t">
            <button
              onClick={handleCancelSession}
              className="px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-sm"
            >
              Annuler inventaire
            </button>
            {/* Produits saisonniers absents : à compter en dernier, une fois le
                rayon réellement compté — ce qui reste à 0 est alors bien absent. */}
            {zeroPendingCount > 0 && (
              <button
                onClick={handleCountZeroLines}
                disabled={confirmingZeros}
                className="px-4 py-2 bg-amber-50 text-amber-800 border border-amber-300 rounded-lg hover:bg-amber-100 disabled:opacity-50 text-sm font-medium"
                title="Confirme à 0 les lignes non comptées dont le stock théorique est déjà nul. Elles restent dans l'inventaire."
              >
                {confirmingZeros
                  ? 'Confirmation…'
                  : `Confirmer les ${zeroPendingCount} produits absents`}
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={handleValidateSession}
              disabled={validating || currentSession.progress < 100}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-semibold"
            >
              {validating ? 'Validation...' : 'Valider inventaire'}
            </button>
          </div>
        )}
        {currentSession.status === 'VALIDATED' && currentSession.pdf_url && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void ouvrirDocumentPrive(currentSession.pdf_url!)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
              >
                Télécharger PDF
              </button>
              <button
                onClick={handleReissuePdf}
                disabled={reissuing}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
                title="Régénère le document en y incluant les explications ajoutées depuis la validation. L'original reste archivé."
              >
                {reissuing ? 'Réédition…' : 'Rééditer avec les explications'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Barre de recherche + filtres */}
      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Rechercher ou scanner un code-barres..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-20 py-3 border border-gray-300 rounded-xl text-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              aria-label="Scanner via caméra"
              title="Scanner via caméra"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h2m12 0h2M4 17h2m12 0h2M3 12h18M7 4h2m6 0h2M7 20h2m6 0h2" />
              </svg>
            </button>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-base"
          >
            <option value="">Toutes catégories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Chips filtres */}
        <div className="flex gap-2">
          {([
            { key: 'to_count' as FilterMode, label: `À compter (${stats.toCount})`, color: 'orange' },
            { key: 'not_counted' as FilterMode, label: `Non comptés (${stats.notCounted})`, color: 'orange' },
            { key: 'counted' as FilterMode, label: `Comptés (${stats.counted})`, color: 'green' },
            { key: 'all' as FilterMode, label: `Tous (${stats.total})`, color: 'gray' },
          ]).map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => setFilterMode(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterMode === key
                  ? color === 'orange'
                    ? 'bg-orange-100 text-orange-800 ring-2 ring-orange-300'
                    : color === 'green'
                    ? 'bg-green-100 text-green-800 ring-2 ring-green-300'
                    : 'bg-gray-200 text-gray-800 ring-2 ring-gray-400'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Liste des produits — cartes tactiles */}
      <div className="space-y-3">
        {productGroups.length === 0 && (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">
            {searchQuery
              ? `Aucun produit trouvé pour "${searchQuery}"`
              : filterMode === 'to_count'
              ? 'Plus rien à compter : tout le stock théorique du périmètre a été vu.'
              : filterMode === 'not_counted'
              ? 'Tous les produits ont été comptés !'
              : 'Aucun produit à afficher'}
          </div>
        )}

        {productGroups.map((group) => (
          <ProductCard
            key={group.productName}
            group={group}
            saving={saving}
            saved={saved}
            disabled={currentSession.status !== 'DRAFT'}
            onUpdateCount={handleUpdateCount}
            onUpdateExpiry={handleUpdateExpiry}
            onUpdateMeta={handleUpdateMeta}
            onAnnotate={currentSession.status === 'VALIDATED' ? handleAnnotate : undefined}
            annotating={annotating}
            ref={(el) => { productRefs.current[group.productName] = el; }}
          />
        ))}
      </div>

      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScan}
        continuous
        title="Scanner les codes-barres (inventaire)"
      />

      {motifsOuverts && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[88vh] flex flex-col shadow-xl">
            <div className="p-4 border-b shrink-0">
              <h3 className="text-lg font-bold text-gray-900">
                Cause des écarts — {ecartsSansMotif.length} ligne
                {ecartsSansMotif.length > 1 ? 's' : ''} sans motif
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Sans cause, l'écart est compté en <strong>démarque inconnue</strong>. « Erreur de
                réception » et « erreur de saisie » en sont exclues : la marchandise n'a jamais été
                là, c'est une correction de donnée.
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <select
                  value={motifEnMasse}
                  onChange={(e) => setMotifEnMasse(e.target.value)}
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-2 bg-white"
                >
                  <option value="">Même cause pour toutes les lignes restantes…</option>
                  {ADJUSTMENT_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    if (!motifEnMasse) return;
                    const tout: Record<string, string> = {};
                    for (const c of ecartsSansMotif) tout[c.id] = motifEnMasse;
                    setMotifsEnCours(tout);
                  }}
                  disabled={!motifEnMasse}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium disabled:opacity-40"
                >
                  Appliquer à toutes
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {ecartsSansMotif.map((c) => {
                const ecart = (c.diff_backroom ?? 0) + (c.diff_display ?? 0);
                return (
                  <div
                    key={c.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 border rounded-lg px-3 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">{c.product_name}</div>
                      <div className="text-xs text-gray-500">
                        {c.lot_number ? `Lot ${c.lot_number} · ` : ''}
                        <span className={ecart < 0 ? 'text-red-600 font-semibold' : 'text-emerald-700 font-semibold'}>
                          {ecart > 0 ? '+' : ''}
                          {Math.round(ecart * 1000) / 1000}
                        </span>{' '}
                        {c.unit || ''}
                      </div>
                    </div>
                    <select
                      value={motifsEnCours[c.id] ?? ''}
                      onChange={(e) =>
                        setMotifsEnCours((prev) => ({ ...prev, [c.id]: e.target.value }))
                      }
                      className="sm:w-56 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white"
                    >
                      <option value="">Motif…</option>
                      {ADJUSTMENT_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t shrink-0 flex flex-col sm:flex-row gap-2 items-center">
              <div className="text-sm text-gray-600 flex-1">
                {Object.values(motifsEnCours).filter(Boolean).length} motif
                {Object.values(motifsEnCours).filter(Boolean).length > 1 ? 's' : ''} à enregistrer
              </div>
              <button
                onClick={() => setMotifsOuverts(false)}
                disabled={enregistrementMotifs}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40"
              >
                Revenir au comptage
              </button>
              <button
                onClick={enregistrerMotifs}
                disabled={
                  enregistrementMotifs || Object.values(motifsEnCours).filter(Boolean).length === 0
                }
                className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium disabled:opacity-40"
              >
                {enregistrementMotifs ? 'Enregistrement…' : 'Enregistrer les motifs'}
              </button>
              <button
                onClick={handleValidateSession}
                disabled={enregistrementMotifs || validating}
                className="px-5 py-2 rounded-lg bg-emerald-600 text-white font-semibold disabled:opacity-40"
              >
                {validating ? 'Validation…' : 'Valider l\'inventaire'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CARTE PRODUIT ──
import { forwardRef } from 'react';
import { ouvrirDocumentPrive } from '../utils/documentPrive';

const ProductCard = forwardRef<HTMLDivElement, {
  group: ProductGroup;
  saving: Record<string, boolean>;
  saved: Record<string, boolean>;
  disabled: boolean;
  onUpdateCount: (count: InventoryCount, backroom: number, display: number) => void;
  onUpdateExpiry: (count: InventoryCount, isoDate: string | null) => void;
  onUpdateMeta: (count: InventoryCount, meta: { adjustment_reason?: string; notes?: string }) => void;
  onAnnotate?: (countId: string, produit: string, ecartEur: string) => void;
  annotating?: string | null;
}>(({ group, saving, saved, disabled, onUpdateCount, onUpdateExpiry, onUpdateMeta, onAnnotate, annotating }, ref) => {
  const isWeightProduct = !!(group.isWeighted || group.unit === 'kg');
  const step = isWeightProduct ? 0.1 : 1;

  const statusBadge = group.allCounted
    ? 'bg-green-100 text-green-700 border-green-200'
    : group.anyCounted
    ? 'bg-orange-100 text-orange-700 border-orange-200'
    : 'bg-gray-100 text-gray-500 border-gray-200';

  const statusLabel = group.allCounted
    ? 'Compté'
    : group.anyCounted
    ? 'En cours'
    : 'Non compté';

  return (
    <div
      ref={ref}
      className={`bg-white rounded-xl shadow transition-all duration-300 overflow-hidden ${
        group.allCounted ? 'border-l-4 border-green-400' : group.anyCounted ? 'border-l-4 border-orange-400' : 'border-l-4 border-gray-200'
      }`}
    >
      {/* En-tête produit */}
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{group.productName}</div>
          <div className="text-xs text-gray-500 flex gap-2 mt-0.5">
            {group.productCode && <span>{group.productCode}</span>}
            {group.category && <span>{group.category}</span>}
            {group.counts.length > 1 && <span>{group.counts.length} lots</span>}
          </div>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-lg border ${statusBadge}`}>
          {statusLabel}
        </span>
      </div>

      {/* Lots */}
      <div className="border-t divide-y divide-gray-100">
        {group.counts.map((count) => (
          <LotRow
            key={count.id}
            count={count}
            step={step}
            isWeight={isWeightProduct}
            isSaving={saving[count.id]}
            isSaved={saved[count.id]}
            disabled={disabled}
            showLotNumber={group.counts.length > 1}
            onUpdate={onUpdateCount}
            onUpdateExpiry={onUpdateExpiry}
            onUpdateMeta={onUpdateMeta}
            onAnnotate={onAnnotate}
            annotating={annotating}
          />
        ))}
      </div>
    </div>
  );
});

ProductCard.displayName = 'ProductCard';

// ── LIGNE DE LOT ──
function LotRow({
  count,
  step,
  isWeight,
  isSaving,
  isSaved,
  disabled,
  showLotNumber,
  onUpdate,
  onUpdateExpiry,
  onUpdateMeta,
  onAnnotate,
  annotating,
}: {
  count: InventoryCount;
  step: number;
  isWeight: boolean;
  isSaving: boolean;
  isSaved: boolean;
  disabled: boolean;
  showLotNumber: boolean;
  onUpdate: (count: InventoryCount, backroom: number, display: number) => void;
  onUpdateExpiry: (count: InventoryCount, isoDate: string | null) => void;
  onUpdateMeta: (count: InventoryCount, meta: { adjustment_reason?: string; notes?: string }) => void;
  /** Défini seulement sur un inventaire VALIDÉ : ajoute une explication datée. */
  onAnnotate?: (countId: string, produit: string, ecartEur: string) => void;
  annotating?: string | null;
}) {
  const currentBackroom = count.counted_backroom ?? 0;
  const currentDisplay = count.counted_display ?? 0;

  const formatVal = (v: number) => (isWeight ? v.toFixed(2) : String(v));
  // Affiche le stock théorique sans bruit flottant (max 3 décimales, zéros superflus retirés)
  const formatStock = (v: number) => String(parseFloat(v.toFixed(3)));
  const [backroomInput, setBackroomInput] = useState(formatVal(currentBackroom));
  const [displayInput, setDisplayInput] = useState(formatVal(currentDisplay));

  useEffect(() => { setBackroomInput(formatVal(currentBackroom)); }, [currentBackroom, isWeight]);
  useEffect(() => { setDisplayInput(formatVal(currentDisplay)); }, [currentDisplay, isWeight]);

  const commitBackroom = () => {
    const parsed = parseFloat(backroomInput.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      setBackroomInput(formatVal(currentBackroom));
      return;
    }
    const rounded = isWeight ? Math.round(parsed * 100) / 100 : Math.round(parsed);
    if (rounded !== currentBackroom) {
      onUpdate(count, rounded, currentDisplay);
    } else {
      setBackroomInput(formatVal(currentBackroom));
    }
  };

  const commitDisplay = () => {
    const parsed = parseFloat(displayInput.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      setDisplayInput(formatVal(currentDisplay));
      return;
    }
    const rounded = isWeight ? Math.round(parsed * 100) / 100 : Math.round(parsed);
    if (rounded !== currentDisplay) {
      onUpdate(count, currentBackroom, rounded);
    } else {
      setDisplayInput(formatVal(currentDisplay));
    }
  };

  const adjustBackroom = (delta: number) => {
    const newVal = Math.round((currentBackroom + delta) * 100) / 100;
    onUpdate(count, Math.max(0, newVal), currentDisplay);
  };

  const adjustDisplay = (delta: number) => {
    const newVal = Math.round((currentDisplay + delta) * 100) / 100;
    onUpdate(count, currentBackroom, Math.max(0, newVal));
  };

  const expiryIso = count.expiry_date
    ? new Date(count.expiry_date).toISOString().split('T')[0]
    : '';

  return (
    <div className="px-4 py-3">
      {/* Info lot + DLC éditable */}
      <div className="flex items-center gap-3 mb-2">
        {showLotNumber && (
          <span className="text-xs text-gray-500 whitespace-nowrap">Lot {count.lot_number}</span>
        )}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-xs text-gray-500 whitespace-nowrap">DLC</span>
          <DLCInput
            value={expiryIso}
            onChange={(iso) => onUpdateExpiry(count, iso || null)}
            compact
            className="w-full"
          />
        </div>
      </div>

      {/* Grille : Réserve | Rayon */}
      <div className="grid grid-cols-2 gap-4">
        {/* RÉSERVE */}
        <div>
          <div className="text-xs text-gray-500 mb-1 flex items-center justify-between">
            <span>Réserve</span>
            <span className="text-gray-400">théo: {formatStock(count.theoretical_backroom)}{isWeight ? ' kg' : ''}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => adjustBackroom(-step)}
              disabled={disabled || isSaving || currentBackroom <= 0}
              className="w-12 h-12 rounded-lg bg-red-50 text-red-700 font-bold text-xl flex items-center justify-center hover:bg-red-100 active:bg-red-200 disabled:opacity-30 transition-colors select-none"
            >
              -
            </button>
            <div className="flex-1 text-center">
              <input
                type={isWeight ? 'text' : 'number'}
                inputMode={isWeight ? 'decimal' : 'numeric'}
                step={step}
                min="0"
                value={backroomInput}
                onChange={(e) => setBackroomInput(e.target.value)}
                onBlur={commitBackroom}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                onFocus={(e) => e.target.select()}
                disabled={disabled || isSaving}
                className={`w-full text-center text-2xl font-bold tabular-nums bg-transparent border border-transparent hover:border-gray-200 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 rounded ${count.is_counted ? 'text-gray-900' : 'text-gray-300'}`}
              />
              {count.is_counted && count.diff_backroom !== undefined && count.diff_backroom !== 0 && (
                <div className={`text-xs font-medium ${count.diff_backroom < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                  {count.diff_backroom > 0 ? '+' : ''}{isWeight ? count.diff_backroom.toFixed(2) : count.diff_backroom}
                </div>
              )}
            </div>
            <button
              onClick={() => adjustBackroom(step)}
              disabled={disabled || isSaving}
              className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-700 font-bold text-xl flex items-center justify-center hover:bg-emerald-100 active:bg-emerald-200 disabled:opacity-30 transition-colors select-none"
            >
              +
            </button>
          </div>
        </div>

        {/* RAYON */}
        <div>
          <div className="text-xs text-gray-500 mb-1 flex items-center justify-between">
            <span>Rayon</span>
            <span className="text-gray-400">théo: {formatStock(count.theoretical_display)}{isWeight ? ' kg' : ''}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => adjustDisplay(-step)}
              disabled={disabled || isSaving || currentDisplay <= 0}
              className="w-12 h-12 rounded-lg bg-red-50 text-red-700 font-bold text-xl flex items-center justify-center hover:bg-red-100 active:bg-red-200 disabled:opacity-30 transition-colors select-none"
            >
              -
            </button>
            <div className="flex-1 text-center">
              <input
                type={isWeight ? 'text' : 'number'}
                inputMode={isWeight ? 'decimal' : 'numeric'}
                step={step}
                min="0"
                value={displayInput}
                onChange={(e) => setDisplayInput(e.target.value)}
                onBlur={commitDisplay}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                onFocus={(e) => e.target.select()}
                disabled={disabled || isSaving}
                className={`w-full text-center text-2xl font-bold tabular-nums bg-transparent border border-transparent hover:border-gray-200 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 rounded ${count.is_counted ? 'text-gray-900' : 'text-gray-300'}`}
              />
              {count.is_counted && count.diff_display !== undefined && count.diff_display !== 0 && (
                <div className={`text-xs font-medium ${count.diff_display < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                  {count.diff_display > 0 ? '+' : ''}{isWeight ? count.diff_display.toFixed(2) : count.diff_display}
                </div>
              )}
            </div>
            <button
              onClick={() => adjustDisplay(step)}
              disabled={disabled || isSaving}
              className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-700 font-bold text-xl flex items-center justify-center hover:bg-emerald-100 active:bg-emerald-200 disabled:opacity-30 transition-colors select-none"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Motif d'écart + remarque (seulement si écart constaté) */}
      {count.is_counted &&
        ((count.diff_backroom ?? 0) !== 0 || (count.diff_display ?? 0) !== 0) && (
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <select
              value={count.adjustment_reason ?? ''}
              onChange={(e) => onUpdateMeta(count, { adjustment_reason: e.target.value })}
              disabled={disabled}
              className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white"
            >
              <option value="">Motif de l'écart…</option>
              {ADJUSTMENT_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <input
              type="text"
              defaultValue={count.notes ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (count.notes ?? '')) onUpdateMeta(count, { notes: v });
              }}
              disabled={disabled}
              placeholder="Remarque (optionnel)"
              className="flex-1 text-sm border border-gray-300 rounded-lg px-2 py-1.5"
            />
          </div>
        )}

      {/* Inventaire VALIDÉ : l'explication se complète après coup, en ajout daté.
          Les quantités comptées, elles, ne se retouchent jamais. */}
      {onAnnotate && (count.adjustment_value ?? 0) !== 0 && (
        <div className="mt-2 flex flex-col gap-1.5">
          {count.notes && (
            <p className="text-xs text-gray-600 whitespace-pre-line bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
              {count.notes}
            </p>
          )}
          <button
            onClick={() =>
              onAnnotate(
                count.id,
                count.product_name || 'ce produit',
                ((count.adjustment_value ?? 0) / 100).toFixed(2) + ' €',
              )
            }
            disabled={annotating === count.id}
            className="self-start text-sm px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            {annotating === count.id ? 'Ajout…' : '+ Expliquer cet écart'}
          </button>
        </div>
      )}

      {/* Indicateur sauvegarde */}
      <div className="h-5 mt-1 flex items-center justify-end">
        {isSaving && (
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Sauvegarde...
          </span>
        )}
        {isSaved && !isSaving && (
          <span className="text-xs text-emerald-600 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Sauvé
          </span>
        )}
      </div>
    </div>
  );
}
